import { httpClient } from "../http-client";
import type { ApiResponse } from "../../../shared/types/api";
import type {
  AutoConfirmOverview,
  AutoConfirmSubscription,
  BankTransferAutoConfirmAccount,
  BankTransferDepositCandidate,
  BankTransferDepositMatchResult,
  BankTransferDepositSummary,
  BankTransferTransaction,
  BillingMethod,
  PrepareAutoConfirmCheckout,
  PreparedAutoConfirmCheckout,
  PreparedBillingMethod,
  UnpaidBankTransferOrder,
  UpdateAutoConfirmAccount,
} from "../../../domain/types/bank-transfer-auto-confirm";

const companyBase = (companyId: number) =>
  `/api/company/${companyId}/bank-transfer-auto-confirm`;

class BankTransferAutoConfirmApiService {
  async overview(companyId: number): Promise<AutoConfirmOverview> {
    const response = await httpClient.get<ApiResponse<AutoConfirmOverview>>(
      companyBase(companyId),
    );
    return response.data.data!;
  }

  async prepare(
    companyId: number,
    command: PrepareAutoConfirmCheckout,
  ): Promise<PreparedAutoConfirmCheckout> {
    const response = await httpClient.post<
      ApiResponse<PreparedAutoConfirmCheckout>
    >(`${companyBase(companyId)}/checkout`, command);
    return response.data.data!;
  }

  async activate(
    companyId: number,
    checkoutKey: string,
    command: { authKey?: string; customerKey: string },
  ): Promise<AutoConfirmSubscription> {
    const response = await httpClient.post<
      ApiResponse<AutoConfirmSubscription>
    >(`${companyBase(companyId)}/checkout/${checkoutKey}/activate`, command);
    return response.data.data!;
  }

  /** 카드 등록·변경 준비. 결제창에 넘길 값만 받는다 */
  async prepareBillingMethod(companyId: number): Promise<PreparedBillingMethod> {
    const response = await httpClient.post<ApiResponse<PreparedBillingMethod>>(
      `${companyBase(companyId)}/billing-method/prepare`,
    );
    return response.data.data!;
  }

  /**
   * 카드 등록·변경 확정.
   * customerKey 를 보내지 않는다 — 서버가 회사에 묶인 값을 쓴다.
   */
  async changeBillingMethod(
    companyId: number,
    authKey: string,
  ): Promise<BillingMethod> {
    const response = await httpClient.put<ApiResponse<BillingMethod>>(
      `${companyBase(companyId)}/billing-method`,
      { authKey },
    );
    return response.data.data!;
  }

  /**
   * 등록된 결제수단 삭제. 청구가 남아 있으면 서버가 거부한다.
   */
  async removeBillingMethod(companyId: number): Promise<BillingMethod> {
    const response = await httpClient.delete<ApiResponse<BillingMethod>>(
      `${companyBase(companyId)}/billing-method`,
    );
    return response.data.data!;
  }

  /**
   * 계좌 자격증명 변경. 연동사 확인이 성공한 뒤에만 저장되므로,
   * 실패하면 기존 정보가 그대로 남는다.
   */
  async updateAccount(
    companyId: number,
    bankTransferId: number,
    command: UpdateAutoConfirmAccount,
  ): Promise<BankTransferAutoConfirmAccount> {
    const response = await httpClient.put<
      ApiResponse<BankTransferAutoConfirmAccount>
    >(`${companyBase(companyId)}/accounts/${bankTransferId}`, command);
    return response.data.data!;
  }

  async cancel(
    companyId: number,
    subscriptionId: number,
  ): Promise<AutoConfirmSubscription> {
    const response = await httpClient.post<
      ApiResponse<AutoConfirmSubscription>
    >(`${companyBase(companyId)}/subscriptions/${subscriptionId}/cancel`);
    return response.data.data!;
  }

  /** 해지 취소. 끊기기 전까지만 열려 있다 — 종료된 계좌는 다시 등록해야 한다 */
  async resume(
    companyId: number,
    subscriptionId: number,
  ): Promise<AutoConfirmSubscription> {
    const response = await httpClient.post<
      ApiResponse<AutoConfirmSubscription>
    >(`${companyBase(companyId)}/subscriptions/${subscriptionId}/resume`);
    return response.data.data!;
  }



  /**
   * 확인된 입금. 서버가 **주문에 연결된 것만** 내려준다 —
   * 연결되지 않은 입금에는 계좌 주인의 개인 거래가 섞여 있다.
   */
  async deposits(
    companyId: number,
    params: { from: string; to: string },
  ): Promise<BankTransferTransaction[]> {
    const response = await httpClient.get<
      ApiResponse<BankTransferTransaction[]>
    >(`${companyBase(companyId)}/transactions`, { params });
    return response.data.data ?? [];
  }

  async depositSummary(
    companyId: number,
    date: string,
  ): Promise<BankTransferDepositSummary> {
    const response = await httpClient.get<
      ApiResponse<BankTransferDepositSummary>
    >(`${companyBase(companyId)}/transactions/summary`, { params: { date } });
    return response.data.data!;
  }

  async unpaidOrders(companyId: number): Promise<UnpaidBankTransferOrder[]> {
    const response = await httpClient.get<
      ApiResponse<UnpaidBankTransferOrder[]>
    >(`${companyBase(companyId)}/transactions/unpaid-orders`);
    return response.data.data ?? [];
  }

  /**
   * 이 주문에 연결할 입금 후보. 주문 하나를 열었을 때만 부른다 —
   * 개인 계좌라면 이자나 개인 송금이 섞여 있어 상시 노출할 목록이 아니다.
   */
  async depositCandidates(
    companyId: number,
    orderId: number,
  ): Promise<BankTransferDepositCandidate[]> {
    const response = await httpClient.get<
      ApiResponse<BankTransferDepositCandidate[]>
    >(`${companyBase(companyId)}/transactions/candidates`, { params: { orderId } });
    return response.data.data ?? [];
  }

  /**
   * 잘못 연결한 입금을 되돌린다. 주문은 결제전으로, 입금은 다시 후보 목록으로 간다.
   */
  async unlink(
    companyId: number,
    transactionId: number,
  ): Promise<BankTransferTransaction> {
    const response = await httpClient.post<
      ApiResponse<BankTransferTransaction>
    >(`${companyBase(companyId)}/transactions/${transactionId}/unlink`);
    return response.data.data!;
  }

  /**
   * 입금 여러 건을 한 주문에 붙인다. 분할 입금이면 함께 골라 합계로 판단한다.
   * 합계가 모자라면 서버가 거부한다 — `allowShortfall` 로만 넘긴다.
   */
  async matchDeposits(
    companyId: number,
    command: {
      orderId: number;
      transactionIds: number[];
      allowShortfall?: boolean;
    },
  ): Promise<BankTransferDepositMatchResult> {
    const response = await httpClient.post<
      ApiResponse<BankTransferDepositMatchResult>
    >(`${companyBase(companyId)}/transactions/match`, command);
    return response.data.data!;
  }


  /**
   * 자동확인 신청. 누르면 바로 쓸 수 있는 상태가 된다 — 승인도 대기도 없다.
   * 뒤에서 연동사 회원사 가입이 자동으로 돈다.
   */
  async apply(companyId: number): Promise<AutoConfirmOverview> {
    const response = await httpClient.post<ApiResponse<AutoConfirmOverview>>(
      `${companyBase(companyId)}/apply`,
      {},
    );
    return response.data.data!;
  }

  /**
   * 쇼핑몰 결제창에 이 계좌를 띄울지.
   * **해지가 아니다** — 꺼도 입금 확인과 요금은 계속된다.
   */
  async setShopVisible(
    companyId: number,
    bankTransferId: number,
    visible: boolean,
  ): Promise<AutoConfirmSubscription> {
    const response = await httpClient.put<ApiResponse<AutoConfirmSubscription>>(
      `${companyBase(companyId)}/accounts/${bankTransferId}/visibility`,
      { visible },
    );
    return response.data.data!;
  }
}

export const bankTransferAutoConfirmApiService =
  new BankTransferAutoConfirmApiService();
